import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import { useCan, Privilege } from '@feedflo/access-control';

import Checkbox from '../../atoms/Checkbox';
import CopyButton from '../../atoms/CopyButton';
import Heading from '../../atoms/Heading';
import Page from '../../atoms/Page';

import { ALGORITHM_VERSIONS_PAGE_GQL } from './queries';

import './AlgorithmVersionsPage.scss';

const DEFAULT_CELL_TEXT = 'None';


function listWithPipes(items, render) {
  if (!items?.length) return DEFAULT_CELL_TEXT;
  return items.map((item, i) => (
    <React.Fragment key={item?.id ?? i}>
      {i > 0 ? ' | ' : null}
      {render(item, item?.id ?? i)}
    </React.Fragment>
  ));
}

export default function AlgorithmVersionsPage() {
  // --- State hooks ---
  const [hideUnusedAlgorithms, setHideUnusedAlgorithms] = useState(false);

  const { canAnywhere, loading: privilegesLoading } = useCan();
  const hasStaffAccess = canAnywhere('feature', Privilege.FEATURE_STAFF_ACCESS);
  const { loading, error, data } = useQuery(ALGORITHM_VERSIONS_PAGE_GQL, {
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
    skip: !hasStaffAccess,
  });

  const algorithmsWithOverrides = useMemo(() => {
    const algorithms = data?.algorithm ?? [];

    const defaultVersion = algorithms
      .filter((a) => a.published_at != null)
      .reduce((latest, alg) => {
        if (!latest || alg.published_at > latest.published_at) return alg;
        return latest;
      }, null)?.version;

    return algorithms.map((alg) => ({
      ...alg,
      isDefault: alg.version === defaultVersion,
      barns: alg.barn_algorithm_overrides?.map((override) => override.barn).filter(Boolean) ?? [],
      devices: alg.device_algorithm_overrides?.map((override) => override.device).filter(Boolean) ?? [],
    }));
  }, [data?.algorithm]);

  const visibleAlgorithms = useMemo(() => {
    if (!hideUnusedAlgorithms) return algorithmsWithOverrides;
    return algorithmsWithOverrides.filter(
      (alg) => alg.isDefault || alg.barns.length > 0 || alg.devices.length > 0,
    );
  }, [algorithmsWithOverrides, hideUnusedAlgorithms]);

  if (!privilegesLoading && !hasStaffAccess) {
    return (
      <Page heading="Algorithm Versions" titleSegments={['Algorithm Versions']}>
        <div className="AlgorithmVersionsPage AlgorithmVersionsPage-loading">
          <Heading level={3}>Access Denied</Heading>
          <p>This page is only available to FeedFlo staff members.</p>
        </div>
      </Page>
    );
  }

  if (loading) {
    return (
      <Page heading="Algorithm Versions" titleSegments={['Algorithm Versions']}>
        <div className="AlgorithmVersionsPage-loading">Loading algorithm versions…</div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page heading="Algorithm Versions" titleSegments={['Algorithm Versions']}>
        <div className="AlgorithmVersionsPage-error">Error loading algorithm versions: {error.message}</div>
      </Page>
    );
  }

  return (
    <Page heading="Algorithm Versions" titleSegments={['Algorithm Versions']}>
      <div className="AlgorithmVersionsPage">
        <div className="AlgorithmVersionsPage-filters">
          <Checkbox
            name="hideUnusedAlgorithms"
            label="Hide unused algorithms"
            checked={hideUnusedAlgorithms}
            onChange={(e) => setHideUnusedAlgorithms(e.target.checked)}
          />
        </div>
        <div className="AlgorithmVersionsPage-table">
          <div className="AlgorithmVersionsPage-tableHeader">
            <div className="AlgorithmVersionsPage-cell AlgorithmVersionsPage-cellVersion">Version / SHA</div>
            <div className="AlgorithmVersionsPage-cell AlgorithmVersionsPage-cellDescription">Details</div>
            <div className="AlgorithmVersionsPage-cell AlgorithmVersionsPage-cellBarns">Barns with overrides</div>
            <div className="AlgorithmVersionsPage-cell AlgorithmVersionsPage-cellDevices">Devices with overrides</div>
          </div>
          {visibleAlgorithms.map((alg) => (
            <div
              key={alg.version}
              className={`AlgorithmVersionsPage-tableRow${alg.isDefault ? ' AlgorithmVersionsPage-tableRow--default' : ''}`}
            >
              <div className="AlgorithmVersionsPage-cell AlgorithmVersionsPage-cellVersion" title={alg.version}>
                <code className="AlgorithmVersionsPage-sha">
                  {alg.version?.length > 12 ? `${alg.version.slice(0, 12)}…` : alg.version || DEFAULT_CELL_TEXT}
                </code>
                {alg.isDefault && <span className="AlgorithmVersionsPage-defaultBadge">Default</span>}
                {alg.version && (
                  <CopyButton
                    data={alg.version}
                    hoverText="Copy full version SHA"
                    className="AlgorithmVersionsPage-copyButton"
                  />
                )}
              </div>
              <div className="AlgorithmVersionsPage-cell AlgorithmVersionsPage-cellDescription">
                {alg.description ?? DEFAULT_CELL_TEXT}
                {alg.published_at != null ? ' (Published)' : ' (Unpublished)'}
              </div>
              <div className="AlgorithmVersionsPage-cell AlgorithmVersionsPage-cellBarns">
                {listWithPipes(alg.barns, (barn, key) =>
                  barn?.id ? (
                    <Link key={key} to={`/barns/${barn.id}/settings`}>
                      {barn.name ?? barn.id}
                    </Link>
                  ) : (
                    <span key={key}>N/A</span>
                  )
                )}
              </div>
              <div className="AlgorithmVersionsPage-cell AlgorithmVersionsPage-cellDevices">
                {listWithPipes(alg.devices, (device, key) =>
                  device?.id ? (
                    <Link key={key} to={`/devices?name=Default+List&sort=nameAsc&d=${device.serial ?? ''}`}>
                      {device.serial ?? device.id}
                    </Link>
                  ) : (
                    <span key={key}>N/A</span>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Page>
  );
}
